import type { ReactNode } from 'react';
import { AccessibilityInfo, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Chip } from '../index';
import { canTrainOneSide, type Exercise } from '@heraion/shared';

import type { DraftEntry, DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import { makeStyles } from './loggerStyles';
import type { CardHints } from './cardHints';
import type { HoldProps } from './useTransientTip';
import { isSetTimed } from './deriveEntryKind';

// FocusPanelChipRow (timeline logger redesign, 2026-05-31) — the qualifier
// chip row of the FocusPanel. Sits BETWEEN the SetCountRow and the inputs
// (Option B, agreed with Antenore via ASCII mockup): "set how the set counts,
// then type the numbers."
//
// It ports the qualifier chips the card logger's EntryChipRow exposes but the
// minimal Phase-2 FocusPanel never carried — the UNI/BI laterality toggle
// (the actual parity gap) and AMRAP. Reuses the card logger's loggerStyles
// chip look so the two loggers read the same. No new write path: BI/UNI →
// set_entry_loaded_sides, AMRAP → an update_set { amrap } patch (all in the
// parent's callbacks). Renders nothing when no chip applies (e.g. cardio with
// a non-modifiable laterality and no bound set). The RIR/RPE effort-column
// toggle left this row in AP-0b — effort is captured by the meter
// (FocusPanelEffort), always on, so there's no column to show/hide.
//
// #308: a tap always does the chip's job; HOLDING it pins its explanation in
// the card's single balloon (FocusPanel owns it, `hold` wires it). The words
// live in cardHints, not here.
export function FocusPanelChipRow({
  entry,
  set,
  exercise,
  onToggleLoadedSides,
  onToggleAmrap,
  onToggleAssisted,
  loadAxisHasValue,
  dropsetActive,
  onToggleBurnPlus,
  burnLocked,
  showTip,
  hold,
  hints,
  leading,
  hideBurnPlus = false,
  hideLaterality = false,
  amrapPrescribing = false,
  onToggleTimed,
  timedActive = false,
  onToggleNote,
  noteActive = false,
  theme,
}: {
  entry: DraftEntry;
  /** The bound set, or null when every set is logged. AMRAP is per-set. */
  set: DraftSet | null;
  /** Catalog exercise for the active entry — drives lateralityModifiable. */
  exercise: Exercise | undefined;
  /** set_entry_loaded_sides (1↔2) + bilateral-preference persist (parent). */
  onToggleLoadedSides: (next: 1 | 2) => void;
  /** Toggles the bound set's DraftSet.amrap, the athlete's INTENTION ("this
   *  set is a test, push it", #539), and may propose the test's load. Returns
   *  what the balloon says about it, or null. */
  onToggleAmrap: () => string | null;
  /** #213: flips the bound set's `extraNegative` (band/machine assist) through
   *  the same onChange path the field uses, so the program editor gets it too. */
  onToggleAssisted: () => void;
  /** False on a prescription axis that carries no kg number (percent of max,
   *  history, effort): there is no load to assist, same honesty as
   *  `hideLaterality`. */
  loadAxisHasValue: boolean;
  /** A Burn+ intensity technique (dropset/myorep) is active on the bound set. */
  dropsetActive: boolean;
  /** Reveal/hide the Dropset|Myo-rep selector bar (state lifted to FocusPanel). */
  onToggleBurnPlus: () => void;
  /** Past-session edit: can't create/change a set-type chain — tap shows a tip. */
  burnLocked: boolean;
  /** The card's balloon, for the laterality explainer and the lock reason. */
  showTip: (message: string, opts?: { pinned?: boolean; explicit?: boolean }) => void;
  /** Hold-for-hint bundle (#308) and the words it shows. */
  hold: HoldProps;
  hints: CardHints;
  /** Leading controls in the SAME wrapping row as the chips: 🕐 history, the
   *  machine tag, the superset, all supplied by the FocusPanel (#308). */
  leading?: ReactNode;
  /** PE-22 K3.1 — prescribe authoring has no dropset/myorep step to run (the
   *  chip's tap is a silent no-op there today), so the mount hides it —
   *  same honesty rule as FocusPanelEffort's `hideFail`. AMRAP + BI/UNI stay
   *  visible: the mockup keeps them, and both are real prescription
   *  commands. */
  hideBurnPlus?: boolean;
  /** PE-35, prescribe: the BI/UNI count (`loadedSides`) has no home in a
   *  program slot, so the chip could only toggle a value the save path throws
   *  away. Which side (`SlotDraft.side`, #75) is another field, set in the
   *  v3 editor. */
  hideLaterality?: boolean;
  /** PE-42 — prescribe: the chip states and writes the coach's REQUEST
   *  (`amrapPrescribed`), not the athlete's outcome (`amrap`), which on a set
   *  nobody has performed is false by construction and made the chip look
   *  permanently off. Same chip, two different facts. */
  amrapPrescribing?: boolean;
  /** #41, "Timed": the bound set's reps⇄time toggle (DraftSet.timed), passed
   *  by the live logger. undefined ⇒ the chip is hidden (same
   *  presence-is-visibility convention as `onSplitGroup`/`onCreateGroup`). */
  onToggleTimed?: () => void;
  timedActive?: boolean;
  /** PE-22 K3.4 — "Nota": opens the per-set note editor, prescribe-editor
   *  only. Same undefined-hides convention as onToggleTimed. */
  onToggleNote?: () => void;
  noteActive?: boolean;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  // Same visibility rule as EntryChipRow: laterality shows on any weighted
  // lift that is intrinsically unilateral (one-arm/one-leg → seeded ×2) OR
  // can be made unilateral (lateralityModifiable). Before 2026-06-09 the
  // intrinsic ones applied ×2 SILENTLY with no chip, so the user couldn't
  // see or override the count — now the chip is always there to read/flip.
  // Cardio/bodyweight have no per-side load to double.
  // #41: a set switched to «Timed» reads the set, here and in the chips below.
  const setTimed = isSetTimed(entry, set);
  const lateralityVisible =
    !hideLaterality &&
    !entry.isCardio &&
    !setTimed &&
    canTrainOneSide(exercise);
  const isUnilateral = entry.loadedSides === 2;

  // AMRAP is a per-set qualifier on strength sets (reps-based). Cardio + timed
  // holds use duration; there's no "as many reps as possible" there. Only
  // meaningful while a set is bound.
  const amrapVisible = set !== null && !entry.isCardio && !setTimed;

  // «Assisted» (#213): bodyweight lifts only, on an axis that holds a kg number.
  const assistVisible =
    set !== null &&
    entry.isBodyweight &&
    loadAxisHasValue &&
    !entry.isCardio &&
    !setTimed &&
    !set.dropset;

  // 🔥 Burn+ (dropset/myorep) — same reach as AMRAP: a per-set intensity
  // technique on strength sets. The chip reveals the Dropset|Myo-rep selector
  // inline (replacing the old ⋯ "Set type" menu); selected = a technique is on.
  const burnPlusVisible =
    set !== null && !entry.isCardio && !setTimed && !hideBurnPlus;

  // "Timed" / "Note": presence of the callback IS the visibility gate.
  // "Timed" (#41, live logging): a set of a reps exercise not yet logged and
  // not a dropset, whose chain has no time form. A timed exercise has nothing
  // to toggle. "Note" applies to any bound set.
  const timedVisible =
    onToggleTimed !== undefined &&
    set !== null &&
    set.completedAt === null &&
    !set.dropset &&
    !entry.isCardio &&
    // The exercise, not isSetTimed: the chip must stay to switch back.
    !entry.isTimeBased;
  const noteVisible = onToggleNote !== undefined;

  // The leading controls are always supplied by the FocusPanel, so the row
  // renders even when no qualifier chip applies. Guard kept for any future
  // caller that omits it.
  if (
    !lateralityVisible &&
    !amrapVisible &&
    !assistVisible &&
    !burnPlusVisible &&
    !timedVisible &&
    !noteVisible &&
    !leading
  )
    return null;

  return (
    // flexWrap inline (not on the shared entryChipRow style — that's the card
    // logger's EntryChipRow too): 🕐 + ? + the qualifier chips (UNI/BI, AMRAP,
    // RIR, 🔥 Burn+) can exceed a narrow panel and must wrap, not clip (≥44pt).
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing.s2 }}>
      {/* The leading controls, then the wrapping qualifier chips. */}
      <View
        style={[
          styles.entryChipRow,
          { flex: 1, flexWrap: 'wrap', gap: theme.spacing.s3 },
        ]}
      >
        {leading}
        {lateralityVisible ? (
        <Chip
          theme={theme}
          dense
          label={isUnilateral ? 'UNI' : 'BI'}
          selected={isUnilateral}
          accessibilityLabel={t(
            isUnilateral ? 'mobile.logger.unilateral' : 'mobile.logger.bilateral',
          )}
          {...hold(hints.laterality(isUnilateral))}
          onPress={() => {
            const next: 1 | 2 = isUnilateral ? 1 : 2;
            onToggleLoadedSides(next);
            // The tap still explains what the new mode counts: the
            // laterality mode is otherwise invisible (2026-06-09).
            showTip(hints.laterality(next === 2));
          }}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      {amrapVisible ? (
        <Chip
          theme={theme}
          dense
          label="AMRAP"
          // ⭐ TWO FACTS, one chip, and which one depends on the mode.
          // In LOG it is the INTENTION (#539): on by itself on a prescribed
          // AMRAP, markable on a set no plan asked for, where it proposes a
          // test and says so in the balloon.
          // In PRESCRIBE (PE-42) there is no outcome (nobody has performed
          // this set), so it states and writes the coach's REQUEST. Reading
          // `amrap` there would have shown the chip permanently off.
          // ⇒ The accessible name says WHICH of the two, because from the
          // glyph alone a screen-reader user moving between the logger and
          // the editor cannot tell a record from a request.
          selected={
            amrapPrescribing ? set?.amrapPrescribed === true : set?.amrap === true
          }
          accessibilityLabel={
            amrapPrescribing
              ? t('mobile.logger.amrapChipPrescribe', {
                  defaultValue: 'Prescribe as many reps as possible',
                })
              : t('mobile.logger.amrapChip', {
                  defaultValue: 'As many reps as possible',
                })
          }
          {...hold(hints.amrap(amrapPrescribing))}
          onPress={() => {
            // `explicit`: the tap changed weight and reps, so the balloon says
            // where they came from even with Hints off (#539 a11y audit).
            const tip = onToggleAmrap();
            if (tip) showTip(tip, { explicit: true });
          }}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      {assistVisible ? (
        <Chip
          theme={theme}
          dense
          role="checkbox"
          label={t('mobile.logger.setExtraAssisted', { defaultValue: 'Assisted' })}
          selected={set?.extraNegative === true}
          {...hold(hints.assisted)}
          onPress={onToggleAssisted}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      {burnPlusVisible ? (
        <Chip
          theme={theme}
          dense
          label={t('mobile.logger.burnPlus', { defaultValue: 'Burn+' })}
          selected={dropsetActive}
          accessibilityLabel={t('mobile.logger.burnPlusChip', {
            defaultValue: 'Burn+ intensity technique (dropset / myo-rep)',
          })}
          {...hold(hints.burnPlus)}
          onPress={() => {
            if (burnLocked) {
              showTip(
                t('mobile.timeline.setTypeLocked', {
                  defaultValue:
                    "Editing a past session: you can't change the set type (dropset/myorep).",
                }),
              );
              return;
            }
            onToggleBurnPlus();
          }}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      {timedVisible ? (
        <Chip
          theme={theme}
          dense
          role="checkbox"
          label={t('mobile.logger.timedChip', { defaultValue: 'Timed' })}
          selected={timedActive}
          {...hold(hints.timed)}
          onPress={() => {
            // #41: the reps field gives way to the duration; say so politely.
            if (!timedActive) AccessibilityInfo.announceForAccessibility(hints.timed);
            onToggleTimed?.();
          }}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      {noteVisible ? (
        <Chip
          theme={theme}
          dense
          label={t('mobile.logger.noteChip', { defaultValue: 'Note' })}
          selected={noteActive}
          accessibilityLabel={t('mobile.logger.noteChip', { defaultValue: 'Note' })}
          {...hold(hints.note)}
          onPress={() => onToggleNote?.()}
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        />
      ) : null}

      </View>
    </View>
  );
}
